import { Inline, Text, vars } from '@etg/wings';
import { useDateFormat, useSearch, useTranslation } from '@eti/providers';
import type { CabinClass, CurrencyAmount, TripType } from '@eti/schema-types';
import { css, resetButtonStyle } from '@eti/styles';
import { date } from '@eti/utils';
import type { MapDispatchToProps, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormStateMap } from 'redux-form';
import { change, formValueSelector, getFormValues } from 'redux-form';
import { useEffect, useRef, useState } from 'react';
import type { MultiBoundType, PassengersType, SingleBoundType } from '../../../../models';
import { SEARCH } from '../../../../../../constants/formNamesConstants';
import {
  DEPARTURE_DATE,
  RETURN_DATE,
  SINGLE_BOUND,
} from '../../../../../../widgets/search-form/constants/formFieldNames';
import { useSearchRedirectConfig } from '../../../../../../widgets/search-form/utils/searchRedirect';
import { handleOnSubmit } from '../../../../../../utils/searchFormSubmit';

const getButtonStyles = (isSelected: Boolean) => css`
  ${resetButtonStyle}
  background-color: #fff;
  border-right: 1px solid ${vars.colors.divider};
  font-size: 0.875rem;
  height: 70px;
  width: 100%;

  :first-child {
    border-bottom-left-radius: ${vars.button.borderRadius};
    border-top-left-radius: ${vars.button.borderRadius};
  }

  :last-child {
    border-bottom-right-radius: ${vars.button.borderRadius};
    border-right: 0;
    border-top-right-radius: ${vars.button.borderRadius};
  }

  :hover {
    background-color: ${vars.colors.inputs.hover};
  }

  ${isSelected &&
  `border: 2px solid ${vars.colors.button.secondary.default.border};
    border-radius: 4px;
    position: relative;
    left: -1px;
    background-color: ${vars.colors.inputs.hover};
  `}
`;

const dateStyles = css`
  margin-bottom: 8px;
`;

const cheapestPriceStyles = css`
  color: #2d7e33;
  font-weight: 700;
`;

export type fieldValuesType = {
  cabinClass: CabinClass;
  carrierCodes?: string[];
  directFlight: boolean;
  multiBound: MultiBoundType[];
  oneDayStopover: boolean;
  oneNightStopover: boolean;
  passengers: PassengersType;
  shortStopover: boolean;
  singleBound: SingleBoundType;
  stopoverEnabled: boolean;
  tripType: typeof TripType.OneWay | typeof TripType.Return;
  twoNightsStopover: boolean;
};

type OwnProps = {
  departureDate: string;
  handleSearch: (keepFilters: boolean) => void;
  isCheapest: boolean;
  isSelected: boolean;
  price: CurrencyAmount;
  returnDate?: string;
};

type ConnectedProps = {
  fieldValues: fieldValuesType;
  currentDepartureDate: string;
};

type ConnectedDispatch = {
  changeDatesInSearchForm: (startDate: string, endDate?: string) => void;
};

const mapStateToProps: MapStateToProps<ConnectedProps, OwnProps, { form: FormStateMap }> = (
  state,
) => {
  return {
    fieldValues: getFormValues(SEARCH)(state) as fieldValuesType,
    currentDepartureDate: formValueSelector(SEARCH)(state, `${SINGLE_BOUND}.${DEPARTURE_DATE}`),
  };
};

const mapDispatchToProps: MapDispatchToProps<ConnectedDispatch, OwnProps> = (dispatch) => ({
  changeDatesInSearchForm: (dateOfDeparture, dateOfReturn) => {
    dispatch(change(SEARCH, `${SINGLE_BOUND}.${DEPARTURE_DATE}`, dateOfDeparture));
    if (dateOfReturn) {
      dispatch(change(SEARCH, `${SINGLE_BOUND}.${RETURN_DATE}`, dateOfReturn));
    }
  },
});

export type NearbyDatePriceProps = OwnProps & ConnectedProps & ConnectedDispatch;

const NearbyDatePrice = ({
  departureDate,
  handleSearch,
  isCheapest,
  isSelected,
  price,
  returnDate,
  currentDepartureDate,
  fieldValues,
  changeDatesInSearchForm,
}: NearbyDatePriceProps) => {
  const { t } = useTranslation();
  const { day } = useDateFormat();
  const { currentSearchData, setSearchData } = useSearch();
  const searchRedirectConfig = useSearchRedirectConfig();
  const [nearbyDatesClicked, setNearbyDatesClicked] = useState(false);
  const travelerTexts = {
    adult: t('Adult'),
    adults: t('Adults'),
    child: t('Child'),
    children: t('Children'),
    infant: t('Infant'),
    infants: t('Infants'),
  };

  const handleClick = () => {
    changeDatesInSearchForm(departureDate, returnDate);
    setNearbyDatesClicked(true);
  };

  const previousValueRef = useRef(currentDepartureDate);

  useEffect(() => {
    if (previousValueRef.current !== currentDepartureDate && nearbyDatesClicked === true) {
      handleOnSubmit(
        () => handleSearch(false),
        travelerTexts,
        currentSearchData,
        setSearchData,
        searchRedirectConfig,
      )(fieldValues);
      setNearbyDatesClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDepartureDate]);

  return (
    <button className={getButtonStyles(isSelected)} onClick={handleClick} type="button">
      <Inline align="center" className={dateStyles}>
        <Text>
          {date.format(departureDate, day)}
          {returnDate && ` – ${date.format(returnDate, day)}`}
        </Text>
      </Inline>
      <Text className={isCheapest && cheapestPriceStyles}>
        {`${price.currency.code} ${price.value}`}
      </Text>
    </button>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NearbyDatePrice);
